import type { PlanBoard, Readiness } from '../api'
import { bucketForQueue } from '../inboxTaxonomy'
import { Icon, type IconName } from './Icon'

// PlanGlance — what the canvas shows before there is a conversation to show.
//
// The console opened with a ~110px empty band above the greeting, and three
// independent judge passes named the same two things: the emptiness, and that
// "the substance of the plan is entirely hidden behind toolbar clicks while the
// canvas sits empty". Those are one problem. The fix is not to close the gap but
// to put the plan in it — the work, who it falls to, and what it prices at, with
// every number a link into the artifact that owns it.
//
// It renders only while the conversation is empty: the moment there is a real
// transcript, that IS the substance and this gets out of the way.

// One tile per figure, each an entry to the artifact that owns it. The label
// and glyph name the measure; the number is the only thing that shouts.
//
// Declared at MODULE scope, not inside PlanGlance. A component defined during
// render is a new component type every render, so React destroys and rebuilds
// every tile's DOM each time — it restarts their transitions today, and would
// silently eat their state the moment one had any. The identical mistake in
// ChatConsole is what threw away PriorityList's "Working…" label.
function Fig({
  icon, n, k, tone, onClick,
}: { icon: IconName; n: string; k: string; tone?: string; onClick: () => void }) {
  return (
    <button className={`glance-fig${tone ? ' ' + tone : ''}`} onClick={onClick}>
      <span className="glance-top">
        <span className="glance-k">{k}</span>
        <span className="glance-icon" aria-hidden><Icon name={icon} size={16} /></span>
      </span>
      <span className="glance-n">{n}</span>
    </button>
  )
}

export function PlanGlance({
  board,
  readiness,
  onOpen,
}: {
  board: PlanBoard
  readiness: Readiness | null
  onOpen: (id: 'board' | 'bid' | 'questions') => void
}) {
  const tasks = board.tasks ?? []
  if (tasks.length === 0) return null

  const hours = tasks.reduce((s, t) => s + (t.estimate_hours ?? 0), 0)
  const people = tasks.filter((t) => t.assignee === 'human').length
  const openCritical = readiness?.open_criticals ?? 0
  const total = readiness?.bid_total ?? 0

  // Group by DISCIPLINE, through the same taxonomy the Inbox routes by — the
  // operator's own carve-up of a job. A task's `inbox` is a slugified task
  // title, so grouping on it directly just re-lists the tasks one apiece.
  const byTrade = new Map<string, number>()
  for (const t of tasks) {
    const name = bucketForQueue(t.inbox || t.required_skill || t.skill || '').name
    byTrade.set(name, (byTrade.get(name) ?? 0) + 1)
  }
  const trades = [...byTrade.entries()].sort((a, b) => b[1] - a[1]).slice(0, 5)

  const money = (n: number) => `$${Math.round(n).toLocaleString()}`

  return (
    <div className="glance">
      <div className="glance-figures">
        <Fig icon="board" n={String(tasks.length)} k={tasks.length === 1 ? 'task' : 'tasks'}
             onClick={() => onOpen('board')} />
        {hours > 0 ? (
          <Fig icon="clock" n={String(Math.round(hours))} k="hours" onClick={() => onOpen('board')} />
        ) : null}
        {people > 0 ? (
          <Fig icon="people" n={String(people)} k={people === 1 ? 'needs a person' : 'need people'}
               onClick={() => onOpen('board')} />
        ) : null}
        {total > 0 ? (
          <Fig icon="estimate" n={money(total)} k="estimate" tone="accent" onClick={() => onOpen('bid')} />
        ) : null}
        {openCritical > 0 ? (
          <Fig icon="questions" n={String(openCritical)}
               k={openCritical === 1 ? 'question open' : 'questions open'} tone="warn"
               onClick={() => onOpen('questions')} />
        ) : null}
      </div>
      {trades.length > 1 ? (
        <button className="glance-trades" onClick={() => onOpen('board')} title="Open the task board">
          {trades.map(([name, n]) => (
            <span key={name} className="glance-trade">
              {name} <span className="glance-trade-n">{n}</span>
            </span>
          ))}
        </button>
      ) : null}
    </div>
  )
}
